/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Label } from '@/features/i18n';
import { useLabels } from '@/hooks/useLabels';
import { infoIcon } from '@/constants/images';
import Hidden from '@/components/Hidden';
import { Headline } from '@/components/Headline/Headline';
import NotificationBlock from '@/components/AZCustomComponent/NotificationBlock/NotificationBlockV2';
import type { AffinityRelatedProductWithPricing } from '@/types';
import NextImage from '@/components/NextImage';
import styles from './styles.module.scss';
import { RelatedPartsPictureList } from './components/RelatedPartsPictureList';
import { RelatedPartsCheckboxNameList } from './components/RelatedPartsCheckboxNameList';
import { AddToCartButtonSection } from './components/AddToCartButtonSection';
import { AffinityErrorNotification } from './components/AffinityErrorNotification';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { type useAddAffinityItemsToCartMutation } from '../../api/postAddAffinityItemsToCart';
import { getErrorDetails, getErrorMessage } from '@/utils/errorsHandling';
import { Fit } from '@az/starc-ui-icons';
import type { MappedAffinityProduct } from '../../interface';
import { Icon, View } from '@az/starc-ui';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useDeviceTypeByMediaQuery } from '@/hooks/useDeviceTypeByMediaQuery';

type Props = {
  affinityRelatedProducts: AffinityRelatedProductWithPricing[];
  addAffinityItemsToCartMutation: ReturnType<typeof useAddAffinityItemsToCartMutation>;
  shippingType: string;
  fulfillmentId: number;
};

export type AddToCartItem = {
  skuId: string;
  productId: string;
  quantity: string;
  fulfillmentTypeId: string;
  vehicleId: string;
  storeNumber: string | undefined;
};

const labelMap = {
  lblCompleteTheJob: 'label_ProductDisplayPage_body_CompleteTheJob',
  lblProductQuantityText: 'label_ProductDisplayPage_body_ProductQuantityText',
  lblFeatureNotAvailable: 'label_FeatureNotAvailable',
  lblInternalServerError: 'error.internal.server.error',
  lblCTJFits: 'label_CTJFits',
};

export const AffinityRelatedProductsBlock = ({
  affinityRelatedProducts,
  shippingType,
  addAffinityItemsToCartMutation,
  fulfillmentId,
}: Props) => {
  const labels = useLabels(labelMap);
  const { acesEnabled } = useAcesFlags();
  const deviceType = useDeviceTypeByMediaQuery();
  const isMobile = deviceType === 'mobile';
  const {
    mutate: addToCartMutation,
    status: addAffinityToCartStatus,
    error: addAffinityToCartError,
  } = addAffinityItemsToCartMutation;
  const productDetailsQuery = useProductDetails();
  const productDetails = productDetailsQuery.isSuccess ? productDetailsQuery.data : null;
  const skuDetailsResult = useProductSkuDetails({
    skuIds:
      productDetailsQuery.isSuccess && productDetails?.product?.itemId
        ? [productDetails.product.itemId]
        : null,
  });
  const skuDetails = skuDetailsResult.isSuccess ? skuDetailsResult.data[0] : null;
  const { data: headerData } = useHeaderData();
  const storeNumber = headerData?.storeNumber;
  const catalogVehicleId = headerData?.vehicleMap?.catalogVehicleId;
  const PreferredVehicle = headerData?.vehicleMap?.vehicleName;
  const [parts, setParts] = useState<MappedAffinityProduct[]>([]);
  const [hasError, setHasError] = useState(false);
  const isNetworkError = addAffinityToCartError?.message === 'Network Error';

  // Reset error state when product changes
  useEffect(() => {
    setHasError(false);
  }, [productDetails?.product?.itemId]);

  useEffect(() => {
    const mappedAffinityRelatedProducts: MappedAffinityProduct[] =
      productDetails && skuDetails
        ? [
            // Current PDP item:
            {
              availableFulfillmentIds: skuDetails.fulfillmentOptions.map(
                (fulfillmentOption) => fulfillmentOption.groupId,
              ),
              brandName: productDetails.product?.brandName ?? '',
              totalPrice: skuDetails.skuPricingAndAvailability.retailPrice ?? null,
              isPartSelected: !skuDetails.itemPresentInCart,
              isAvailable:
                skuDetails.fulfillmentOptions.some((option) => option.available) &&
                // Check that price is not null or 0
                Boolean(skuDetails.skuPricingAndAvailability.retailPrice),
              productImageUrl: productDetails.product?.itemImageUrl ?? '',
              productName: productDetails.product?.itemDescription ?? '',
              skuId: productDetails.product?.itemId ?? '',
              productId: acesEnabled
                ? (productDetails.product?.partGroupId ?? '')
                : (productDetails.product?.ecommProductId ??
                  productDetails.product?.originalPartTypeId ??
                  ''),

              isMainItem: true,
              originalPartTypeId: productDetails.product?.originalPartTypeId ?? '',
              partGroupId: productDetails.product?.partGroupId ?? '',
              partTerminologyId: productDetails.product?.ecommProductId ?? '',
              isInCart: skuDetails.itemPresentInCart,
              vehicleFit: productDetails.product?.vehicleFit,
            },
            // Affinity items:
            ...affinityRelatedProducts.map(
              ({
                brandName,
                availableFulfillmentIds,
                totalPrice,
                productImageUrl,
                productName,
                skuId,
                seoUrl,
                productId,
                originalPartTypeId,
                partTerminologyId,
                vehicleFit,
                vehicleFitment,
              }) => ({
                availableFulfillmentIds,
                totalPrice,
                productImageUrl,
                productName,
                skuId,
                seoUrl,
                productId,
                isAvailable: totalPrice !== null,
                isPartSelected: true,
                isMainItem: false,
                originalPartTypeId,
                partGroupId: productId,
                partTerminologyId,
                brandName,
                vehicleFit,
                vehicleFitment,
              }),
            ),
          ]
        : [];
    setParts(mappedAffinityRelatedProducts);
  }, [skuDetails, productDetails, affinityRelatedProducts, acesEnabled]);

  useEffect(() => {
    if (addAffinityToCartStatus === 'error') {
      setHasError(true);
      const unavailableSkus = getErrorDetails(addAffinityToCartError)?.map(
        (part) => part.errorCodeDescription,
      );
      setParts((prevParts) =>
        prevParts.map((part) => {
          if (part.skuId && unavailableSkus?.includes(part.skuId)) {
            part.isPartSelected = false;
            part.isAvailable = false;
          }

          return part;
        }),
      );
    } else {
      setHasError(false);
    }
  }, [addAffinityToCartStatus, addAffinityToCartError]);

  const isPDPItemAlreadyInCart = useRef(false);

  // Reset ref when product changes
  useEffect(() => {
    isPDPItemAlreadyInCart.current = false;
  }, [productDetails?.product?.itemId]);

  useEffect(() => {
    // skuDetails will return itemPresentInCart just for the main pdp item
    if (!isPDPItemAlreadyInCart.current && skuDetails?.itemPresentInCart) {
      setParts((prevParts) =>
        prevParts.map((part) => {
          if (part.isMainItem) {
            part.isPartSelected = false;
            part.isInCart = true;
            isPDPItemAlreadyInCart.current = true;
          }

          return part;
        }),
      );
    }
  }, [parts, skuDetails?.itemPresentInCart]);

  const { amountOfSelectedItems, itemsTotalPrice } = parts.reduce(
    (acc, { isPartSelected, isAvailable, totalPrice }) => ({
      itemsTotalPrice:
        acc.itemsTotalPrice +
        (isAvailable && isPartSelected && totalPrice !== null ? totalPrice : 0),
      amountOfSelectedItems: acc.amountOfSelectedItems + (isAvailable && isPartSelected ? 1 : 0),
    }),
    {
      itemsTotalPrice: 0,
      amountOfSelectedItems: 0,
    },
  );

  const handleSelect = (skuId: string | null) => {
    setParts((prevParts) =>
      prevParts.map((part) => {
        if (part.skuId === skuId && part.isAvailable) {
          return { ...part, isPartSelected: !part.isPartSelected };
        }

        return part;
      }),
    );
  };

  const handleAddToCart = () => {
    const selectedAndAvailableParts = parts.filter(
      ({ isPartSelected, isAvailable }) => isAvailable && isPartSelected,
    );

    addToCartMutation({
      selectedAndAvailableParts,
      allPartsAmount: parts.length,
      storeNumber,
      catalogVehicleId,
      shippingType,
      fulfillmentId,
    });
  };

  const handleCloseErrorNotification = () => {
    setHasError(false);
  };

  // Check if the main product part (PDP item) fits the preferred vehicle
  const mainProductFits = parts.find((part) => part.isMainItem)?.vehicleFit === 'FITS';
  const atleastOneCTJFits = parts.some(
    (part) => part.vehicleFitment === 'VEHICLE_SPECIFIC' && part.vehicleFit === 'FITS',
  );
  const showCTJFits = mainProductFits && atleastOneCTJFits;

  return parts.length ? (
    <div className={styles.relatedProductsContainer}>
      <div className={styles.headerContainer}>
        <View padding={{ s: [0, 0, 4, 0] }}>
          <Headline className={styles.header} size="175">
            {labels.lblCompleteTheJob}
          </Headline>
        </View>
        {showCTJFits && (
          <div className={styles.ctjFitsBadgingContainer}>
            <div className={styles.ctjFitsBadging}>
              <Icon svg={Fit} size={isMobile ? 6 : 7} aria-hidden="true" />
              <text className={styles.fitsText}>{labels.lblCTJFits}</text>{' '}
              <text className={styles.vehicleName}>{PreferredVehicle}</text>
            </div>
          </div>
        )}
      </div>
      {hasError ? (
        getErrorDetails(addAffinityToCartError)?.length &&
        getErrorMessage(addAffinityToCartError) ? (
          <AffinityErrorNotification
            error={addAffinityToCartError}
            onClick={handleCloseErrorNotification}
          />
        ) : (
          <NotificationBlock
            type="error"
            message={
              <Label
                label={
                  isNetworkError
                    ? labels.lblInternalServerError
                    : (getErrorMessage(addAffinityToCartError) ?? labels.lblFeatureNotAvailable)
                }
              />
            }
            notificationContainerClassName={isNetworkError ? styles.networkError : ''}
          />
        )
      ) : null}
      <div className={styles.relatedPartsBody}>
        <div className={styles.relatedPartsPictureListContainer}>
          <RelatedPartsPictureList parts={parts} />
          <Hidden mdDown>
            <AddToCartButtonSection
              addAffinityToCartStatus={addAffinityToCartStatus}
              amountOfSelectedItems={amountOfSelectedItems}
              itemsTotalPrice={itemsTotalPrice}
              onClick={handleAddToCart}
            />
          </Hidden>
        </div>
        <div className={styles.productQuantityInfo}>
          <div className={styles.productQuantityInfoImageWrapper}>
            <NextImage src={infoIcon.src} alt={infoIcon.alt} height={20} width={20} />
          </div>
          <span className={styles.productQuantityInfoLabel}>{labels.lblProductQuantityText}</span>
        </div>
        <RelatedPartsCheckboxNameList
          parts={parts}
          handleSelect={handleSelect}
          isPDPItemInCart={isPDPItemAlreadyInCart.current}
        />
        <Hidden lgUp>
          <AddToCartButtonSection
            addAffinityToCartStatus={addAffinityToCartStatus}
            amountOfSelectedItems={amountOfSelectedItems}
            itemsTotalPrice={itemsTotalPrice}
            onClick={handleAddToCart}
          />
        </Hidden>
      </div>
    </div>
  ) : null;
};
